import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import moment from "moment";
import { alpha, Checkbox, Chip, Grid, Tooltip } from "@mui/material";
import { useState } from "react";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import useUpdateServicerStatus from "../../../../src/hooks/trainingHooks/useUpdateServicerStatus";
import { ApproveOrReject, UserRole } from "../../../utils/consts";
import "../../../utils/timezone";

export const renderTableCellForServicerStatus = (trsiiOptIn,optOutFlag, userRole) => {
  const chipStyle = {
    width: '112px',
    color: "default",
    variant: 'outlined',
  };

  const getServicerStatus = (trsiiOptIn, optOutFlag, userRole) => {
    if (userRole === UserRole.APPROVER) {
      if (trsiiOptIn === 0 && optOutFlag === 1) {
        return { status: ApproveOrReject.APPROVE, color: 'success' };
      } else if (trsiiOptIn === 1 && optOutFlag === 1) {
        return { status: ApproveOrReject.REJECT, color: 'error' };
      } else if (trsiiOptIn === 0 && optOutFlag === 0) {
        return { status: ApproveOrReject.SUBMITTED, color: 'primary', variant: 'filled' };
      }
    } else if (userRole === UserRole.ADMIN) {
      if (trsiiOptIn === 0 && optOutFlag === 1) {
        return { status: 'Opted in for Public Scoring', width: '200px' };
      } else if (trsiiOptIn === 1 && optOutFlag === 1) {
        return { status: 'Rejected by HUD', width: '200px' };
      } else if (trsiiOptIn === 0 && optOutFlag === 0) {
        return { status: 'Submitted for HUD approval', width: '200px' };
      }
    }
    return null;
  };

  const { status, color, variant, width } = getServicerStatus(trsiiOptIn, optOutFlag, userRole) || {};

  if (!status) {
    return null;
  }

  return (
    <TableCell align="left">
      <Chip
        sx={{ width: width || chipStyle.width }}
        variant={variant || chipStyle.variant}
        color={color || chipStyle.color}
        label={status}
      />
    </TableCell>
  );
};


const ServicerTableForApprover = ({ servicerList, userRole }) => {
  const [servicersSelected, setServicersSelected] = useState([]);

  // TODO: bug need to be fixed
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = servicerList.map((servicer) => servicer.id);

      setServicersSelected(newSelected.filter((item) => !!item));
      return;
    }
    setServicersSelected([]);
  };

  const handleClick = (event, name, servicerStatus) => {
    const selectedIndex = servicersSelected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(servicersSelected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(servicersSelected.slice(1));
    } else if (selectedIndex === servicersSelected.length - 1) {
      newSelected = newSelected.concat(servicersSelected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        servicersSelected.slice(0, selectedIndex),
        servicersSelected.slice(selectedIndex + 1)
      );
    }

    setServicersSelected(newSelected);
  };

  const isSelected = (name) => servicersSelected.indexOf(name) !== -1;
  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
      <EnhancedTableToolbar
          numSelected={servicersSelected.length}
          servicersSelected={servicersSelected}
          setServicersSelected={setServicersSelected}
        />
        <TableContainer>
          <Table sx={{ minWidth: 900 }} aria-labelledby="tableTitle">
            <EnhancedTableHead
              numSelected={servicersSelected.length}
              //onSelectAllClick={(e) => handleSelectAllClick(e)}
              rowCount={servicerList.length}
            />
            <TableBody>
              {servicerList.map((servicer, index) => {
                const {
                  id: servicerId,
                  servicerMasterName,
                  trsiiOptIn,
                  optOutFlag,
                  approvedDate,
                } = servicer;
                const isItemSelected = isSelected(servicerId);
                const labelId = `enhanced-table-checkbox-${index}`;
              
                return (
                  <TableRow
                    hover
                    onClick={(event) =>
                      handleClick(event, servicerId, trsiiOptIn)
                    }
                    //role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={servicerId}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        // disabled={servicerStatus.toLowerCase() !== 'pending'}
                        inputProps={{
                          "aria-labelledby": labelId,
                        }}
                      />
                    </TableCell>
                    <TableCell component="th" scope="row" align="left">
                      {servicerId}
                    </TableCell>
                    <TableCell align="left">{servicerMasterName}</TableCell>
                    <TableCell align="left">{moment(approvedDate).format('MM-DD-YYYY')}</TableCell>
                    <TableCell align="left">{renderTableCellForServicerStatus(trsiiOptIn,optOutFlag, userRole)}
                    </TableCell>

                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

ServicerTableForApprover.propTypes = {
    servicerList: PropTypes.arrayOf(
      PropTypes.shape({
        // Define the expected shape of each servicer object
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        // Add more properties as needed
      })
    ).isRequired,
    userRole: PropTypes.string.isRequired,
};

const headCells = [
  { label: "Servicer ID" },
  { label: "Servicer Name" },
  {label: "Approved Date"},
  { label: "Opt Out Status" },
];

function EnhancedTableHead({ onSelectAllClick, numSelected, rowCount }) {
  return (
    <TableHead>
      <TableRow
        sx={{
          backgroundColor: `rgba(210, 174, 27, 0.1)`,
          fontWeight: 1000,
          borderBottom: "initial",
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              "aria-label": "select all desserts",
            }}
          />
        </TableCell>
        {headCells.map((headCell, index) => (
          <TableCell key={index} sx={{ pr: "0px" }}>
            <Typography variant="subtitle" noWrap>
              <b>{headCell.label}</b>
            </Typography>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar({
  numSelected,
  servicersSelected,
  setServicersSelected,
}) {
  const { mutate: updateServicerStatus } =
    useUpdateServicerStatus(setServicersSelected);

  const approveServicers = () => {
    updateServicerStatus({
      servicerIds: servicersSelected,
      approveOrReject: ApproveOrReject.APPROVE,
    });
  };
  const rejectServicers = () => {
    updateServicerStatus({
      servicerIds: servicersSelected,
      approveOrReject: ApproveOrReject.REJECT,
    });
  };

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
      }}
    >
      {numSelected > 0 && (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      )}

      {numSelected > 0 && (
        <Grid
          container
          alignItems="center"
          justifyContent="flex-end"
          spacing={1}
        >
          <Grid item>
            <Tooltip title="Approve">
              <Button
                variant="contained"
                disableElevation
                size="small"
                onClick={() => approveServicers()}
              >
                Approve
              </Button>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Reject">
              <Button
                variant="contained"
                disableElevation
                color="error"
                size="small"
                onClick={() => rejectServicers()}
              >
                Reject
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      )}
    </Toolbar>
  );
}

export default ServicerTableForApprover;
